import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

interface FrequencyCounterProps {
  targetBehavior: string;
  onSave?: (count: number, duration: number, notes: string) => void;
}

interface SessionData {
  count: number;
  startTime: Date;
  endTime?: Date;
  notes: string;
}

// ═══════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════

const FrequencyCounter: React.FC<FrequencyCounterProps> = ({
  targetBehavior,
  onSave,
}) => {
  const [count, setCount] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [notes, setNotes] = useState('');

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isActive) {
      interval = setInterval(() => {
        setElapsed(Date.now() - (startTime?.getTime() || Date.now()));
      }, 1000);
    }
    
    return () => clearInterval(interval);
  }, [isActive, startTime]);

  const formatTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
  };

  const handleIncrement = useCallback(() => {
    setCount((prev) => prev + 1);
  }, []);

  const handleDecrement = useCallback(() => {
    setCount((prev) => Math.max(0, prev - 1));
  }, []);

  const handleStart = useCallback(() => {
    setIsActive(true);
    setStartTime(new Date());
    setElapsed(0);
  }, []);

  const handleStop = useCallback(() => {
    setIsActive(false);
    setShowSaveDialog(true);
  }, []);

  const handleReset = useCallback(() => {
    setCount(0);
    setIsActive(false);
    setStartTime(null);
    setElapsed(0);
    setNotes('');
  }, []);

  const handleSave = useCallback(() => {
    const duration = Math.floor(elapsed / 1000 / 60); // Convert to minutes
    
    if (onSave) {
      onSave(count, duration, notes);
    }
    
    handleReset();
    setShowSaveDialog(false);
  }, [count, elapsed, notes, onSave, handleReset]);

  const rate = elapsed > 0 ? ((count / (elapsed / 1000 / 60)) * 60).toFixed(1) : '0';

  return (
    <>
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Frequency Counter
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Target: {targetBehavior}
        </Typography>

        {/* Timer Display */}
        <Box sx={{ mb: 3 }}>
          <Chip 
            icon={isActive ? <StopIcon /> : <StartIcon />}
            label={isActive ? formatTime(elapsed) : 'Not Started'}
            color={isActive ? 'success' : 'default'}
            size="medium"
            sx={{ fontSize: '1.1rem', py: 1 }}
          />
        </Box>

        {/* Count Display */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 3,
            mb: 3 
          }}
        >
          <IconButton 
            onClick={handleDecrement}
            disabled={count === 0 || isActive}
            size="large"
            sx={{ 
              width: 56, 
              height: 56,
              backgroundColor: 'action.hover'
            }}
          >
            <RemoveIcon />
          </IconButton>

          <Box 
            sx={{ 
              minWidth: 120,
              py: 2,
              px: 4,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2,
            }}
          >
            <Typography variant="h2" fontWeight={700}>
              {count}
            </Typography>
            <Typography variant="caption">
              occurrences
            </Typography>
          </Box>

          <IconButton 
            onClick={handleIncrement}
            disabled={!isActive}
            size="large"
            color="primary"
            sx={{ 
              width: 56, 
              height: 56,
              backgroundColor: 'primary.light'
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>

        {/* Rate Display */}
        {isActive && count > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rate: {rate} per hour
          </Typography>
        )}

        {/* Action Buttons */}
        <Grid container spacing={2} justifyContent="center">
          {!isActive ? (
            <Grid item>
              <Button
                variant="contained"
                size="large"
                startIcon={<StartIcon />}
                onClick={handleStart}
              >
                Start Session
              </Button>
            </Grid>
          ) : (
            <>
              <Grid item>
                <Button
                  variant="contained"
                  size="large"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                >
                  End & Save
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleReset}
                >
                  Cancel
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Save Dialog */}
      <Dialog 
        open={showSaveDialog} 
        onClose={() => setShowSaveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Frequency Data</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>Behavior:</strong> {targetBehavior}
            </Typography>
            <Typography variant="body1">
              <strong>Count:</strong> {count} occurrences
            </Typography>
            <Typography variant="body1">
              <strong>Duration:</strong> {formatTime(elapsed)}
            </Typography>
            <Typography variant="body1">
              <strong>Rate:</strong> {rate} per hour
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any observations during the session..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save Data
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FrequencyCounter;
